/**
 * PasswordInput - password field with a show/hide toggle.
 *
 * Deliberately NOT ImagoInput-shaped: 12px text with px-2.5 py-3 padding and an
 * 11px label, because it only ever appears inside AuthCard, which runs its own
 * more generous scale.
 */

import React, { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';

/**
 * @param {Object} props
 * @param {string} props.id - input ID
 * @param {string} props.value
 * @param {Function} props.onChange - called with the raw string value
 * @param {string} [props.placeholder='Enter your password']
 * @param {string} [props.label='Password']
 * @param {boolean} [props.required]
 * @param {string} [props.className] - container classes
 * @param {Object} [props.inputStyle] - custom inline styles for the input
 */
export function PasswordInput({
  id,
  value,
  onChange,
  placeholder = 'Enter your password',
  label = 'Password',
  required = false,
  disabled = false,
  className = '',
  inputStyle = {},
  ...rest
}) {
  const [isVisible, setIsVisible] = useState(false);
  const generatedId = React.useId();
  const inputId = id || `imago-password-${generatedId}`;

  const handleChange = (e) => {
    onChange(e.target.value);
  };

  return (
    <div className={`flex flex-col gap-1 min-w-0 ${className}`}>
      {label && (
        <label htmlFor={inputId} className="text-xs font-medium text-foreground">
          {label}
        </label>
      )}
      <div className="relative flex items-center">
        <input
          type={isVisible ? 'text' : 'password'}
          id={inputId}
          value={value}
          onChange={handleChange}
          placeholder={placeholder}
          required={required}
          disabled={disabled}
          className="w-full pl-2.5 pr-10 py-3 text-xs rounded-md bg-input text-foreground border border-input-border placeholder:text-muted transition-[border-color,box-shadow] duration-base ease-standard focus:outline-none focus:border-primary focus:shadow-focus-inset disabled:opacity-50 disabled:cursor-not-allowed"
          style={inputStyle}
          {...rest}
        />
        <button
          type="button"
          onClick={() => setIsVisible((v) => !v)}
          className="absolute right-3 flex text-muted transition-colors duration-base ease-standard hover:text-foreground focus:outline-none"
          aria-label={isVisible ? 'Hide password' : 'Show password'}
        >
          {isVisible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}

export default PasswordInput;
